import ContactForm from "../components/ContactForm";
import Hero from "../components/Hero";
import contactImg from "../assests/images/contact-bg.jpg";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaChevronDown,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";

export default function ContactUs() {
  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      value: "Office 302, 3rd Flr. HDS Tower Cluster F, Jumeirah Lakes Towers Dubai",
      description: "Our travel team is based in Dubai.",
    },
    {
      icon: <FaPhoneAlt />,
      title: "Call Us",
      value: "+971 123 456 789",
      description: "Speak directly with our support team.",
      link: "tel:+971123456789",
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      value: "info@southtravels.com",
      description: "Send us your questions anytime.",
      link: "mailto:info@southtravels.com",
    },
  ];

  const faqs = [
    {
      question: "How fast do you respond?",
      answer:
        "Our team usually responds to inquiries within 24 hours.",
    },
    {
      question: "Can I customize my tour?",
      answer:
        "Yes. We can help arrange customizable tours and travel services according to your requirements.",
    },
    {
      question: "Can you help me choose a hotel?",
      answer:
        "Absolutely. Our team can help you find suitable hotels based on your preferred location, budget and travel requirements.",
    },
    {
      question: "Do you provide travel assistance?",
      answer:
        "Yes. Our team can assist with hotel bookings and other travel-related inquiries.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us | Jumeira Discount Hotels</title>

        <meta
          name="description"
          content="Contact Jumeira Discount Hotels for hotel bookings, travel assistance, affordable stay options, and customer support in Dubai."
        />

        <link
          rel="canonical"
          href="https://jumeiradiscounthotels.com/contact-us"
        />

        <meta
          property="og:title"
          content="Contact Us | Jumeira Discount Hotels"
        />

        <meta
          property="og:description"
          content="Contact Jumeira Discount Hotels for hotel bookings, travel assistance, affordable stay options, and customer support in Dubai."
        />

        <meta
          property="og:url"
          content="https://jumeiradiscounthotels.com/contact-us"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Contact Us | Jumeira Discount Hotels"
        />

        <meta
          name="twitter:description"
          content="Contact Jumeira Discount Hotels for hotel bookings, travel assistance, affordable stay options, and customer support in Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="w-full bg-[#f7f9fb] overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <Hero
          variant="inner"
          title="Let's Talk About Your Next Stay"
          subtitle="Have a question about hotels, bookings or travel? Our team is ready to help."
          backgroundImage={contactImg}
        />

        {/* =====================================================
            CONTACT INFO
        ====================================================== */}
        <section className="relative -mt-8 sm:-mt-12 z-20 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">

            <div className="bg-white rounded-[28px] sm:rounded-[36px] shadow-[0_20px_60px_rgba(15,23,42,0.12)] border border-slate-100 overflow-hidden">

              <div className="grid grid-cols-1 md:grid-cols-3">

                {contactInfo.map((item, index) => {
                  const content = (
                    <div
                      className={`
                        group relative p-6 sm:p-8 lg:p-10
                        transition-all duration-300
                        hover:bg-slate-50
                        ${index !== contactInfo.length - 1
                          ? "md:border-r border-b md:border-b-0 border-slate-100"
                          : ""
                        }
                      `}
                    >

                      {/* Icon */}
                      <div className="flex items-start gap-4">

                        <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#eef3f6] text-[#002B45] flex items-center justify-center text-lg group-hover:bg-[#002B45] group-hover:text-white transition-all duration-300">
                          {item.icon}
                        </div>

                        <div className="min-w-0">
                          <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#D4A83F]">
                            {item.title}
                          </p>

                          <h3 className="mt-1 font-bold text-lg text-slate-900 break-words">
                            {item.value}
                          </h3>

                          <p className="text-sm text-slate-500 mt-1 leading-5">
                            {item.description}
                          </p>
                        </div>

                      </div>

                    </div>
                  );

                  return item.link ? (
                    <a key={index} href={item.link}>
                      {content}
                    </a>
                  ) : (
                    <div key={index}>{content}</div>
                  );
                })}

              </div>

            </div>

          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 sm:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

              {/* LEFT — Let's Talk */}
              <div className="lg:col-span-4">

                <div className="lg:sticky lg:top-28">

                  <span className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 text-[#0F172A] text-sm font-semibold mb-5">
                    Let's Talk
                  </span>

                  <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                    Have a Question?
                    <span className="block text-[#174B5F]">
                      We're Here to Help.
                    </span>
                  </h2>

                  <p className="mt-5 text-slate-500 leading-7">
                    Whether you need help finding the perfect hotel, have a booking
                    question, or simply want to know more about our services, our
                    team is ready to assist you.
                  </p>

                  {/* Contact Details */}
                  <div className="mt-8 space-y-4">

                    <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                      <div className="w-12 h-12 rounded-xl bg-orange-100 text-[#0F172A] flex items-center justify-center shrink-0">
                        <FaMapMarkerAlt />
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                          Location
                        </p>
                        <p className="font-semibold text-slate-800">
                          Dubai, UAE
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                      <div className="w-12 h-12 rounded-xl bg-orange-100 text-[#0F172A] flex items-center justify-center shrink-0">
                        <FaPhoneAlt />
                      </div>

                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                          Phone
                        </p>

                        <a
                          href="tel:+971123456789"
                          className="font-semibold text-slate-800 hover:text-[#174B5F] transition"
                        >
                          +971 123 456 789
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                      <div className="w-12 h-12 rounded-xl bg-orange-100 text-[#0F172A] flex items-center justify-center shrink-0">
                        <FaEnvelope />
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                          Email
                        </p>

                        <a
                          href="mailto:info@southtravels.com"
                          className="font-semibold text-slate-800 hover:text-[#174B5F] transition break-all"
                        >
                          info@southtravels.com
                        </a>
                      </div>
                    </div>

                  </div>

                  {/* Small CTA */}
                  <div className="mt-6 rounded-2xl bg-gradient-to-r from-[#002B45] via-[#174B5F] to-[#D4A83F] p-5 text-white">
                    <p className="text-sm font-semibold">
                      Need immediate assistance?
                    </p>

                    <p className="text-white/75 text-sm mt-1">
                      Our team is ready to help with your hotel booking.
                    </p>
                  </div>

                </div>

              </div>


              {/* RIGHT — FULL CONTACT FORM */}
              <div className="lg:col-span-8">

                <div className="bg-white rounded-[32px] border border-slate-100 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-5 sm:p-8 lg:p-10">

                  <div className="mb-8">
                    <span className="text-sm font-semibold text-[#174B5F] uppercase tracking-wider">
                      Send Us a Message
                    </span>

                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
                      How Can We Help?
                    </h3>

                    <p className="text-slate-500 mt-2">
                      Fill out the form below and our team will get back to you.
                    </p>
                  </div>

                  {/* Your existing form */}
                  <ContactForm />

                </div>

              </div>

            </div>

          </div>
        </section>

        {/* =====================================================
            MAP SECTION
        ====================================================== */}
        <section className="pb-16 sm:pb-20 lg:pb-28">

          <div className="max-w-7xl mx-auto px-4 sm:px-6">

            <div className="mb-8">

              <span className="text-[#D4A83F] text-sm font-bold uppercase tracking-[0.15em]">
                FIND US
              </span>

              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
                We're in Dubai
              </h2>

              <p className="text-slate-500 mt-2">
                Find our location and get directions to our office.
              </p>

            </div>

            <div className="relative rounded-[30px] sm:rounded-[36px] overflow-hidden shadow-[0_20px_60px_rgba(15,23,42,0.12)] border-4 border-white">

              <iframe
                src="https://www.google.com/maps?q=Dubai&output=embed"
                className="w-full h-[300px] sm:h-[400px] lg:h-[480px]"
                loading="lazy"
                title="Beach Hotels Dubai Location"
              ></iframe>

              {/* Map Label */}
              <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4 shadow-xl">

                <div className="flex items-center gap-3">

                  <div className="w-10 h-10 rounded-xl bg-[#002B45] text-white flex items-center justify-center">
                    <FaMapMarkerAlt />
                  </div>

                  <div>
                    <p className="font-bold text-slate-900 text-sm">
                      Dubai, UAE
                    </p>

                    <p className="text-xs text-slate-500">
                      United Arab Emirates
                    </p>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-16 sm:py-20 lg:py-24 bg-white">

          <div className="max-w-4xl mx-auto px-4 sm:px-6">

            <div className="text-center mb-10 sm:mb-14">

              <span className="inline-flex px-4 py-2 rounded-full bg-[#eef3f6] text-[#002B45] text-xs sm:text-sm font-bold">
                FAQ
              </span>

              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                Frequently Asked Questions
              </h2>

              <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
                Quick answers to some of the questions our travelers ask most.
              </p>

            </div>

            <div className="space-y-3">

              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-[#f7f9fb] border border-slate-100 rounded-2xl overflow-hidden"
                >

                  <summary className="list-none cursor-pointer flex items-center justify-between gap-4 px-5 sm:px-6 py-5 font-semibold text-slate-900">

                    <span>{faq.question}</span>

                    <span className="shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#002B45] shadow-sm group-open:rotate-180 transition-transform duration-300">
                      <FaChevronDown className="text-xs" />
                    </span>

                  </summary>

                  <div className="px-5 sm:px-6 pb-5 text-sm sm:text-base text-slate-500 leading-6">
                    {faq.answer}
                  </div>

                </details>
              ))}

            </div>

          </div>

        </section>

      </div>
    </>
  );
}